import React from 'react';
import type { Vehicle } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { VehicleDynamicIcon } from '@hero/web-icons';
import { getVehicleBrandLabel, useVehicleDrawerStore } from '@hero/hooks-shared';
import { TableCell } from '../../../ui/Table/Table';
import { MianAvatar } from '../../../app/MianAvatar';
import { CellAction } from '../../../app/Table/CellAction';
import {
  AvailabilityChip,
  formatPlateForDisplay,
  vehicleStatusChip,
} from '../../../app/Select/SelectAsync/selectorEntityRowUtils';

export interface VehicleRowProps {
  vehicle: Vehicle;
}

export const VehicleRow: React.FC<VehicleRowProps> = ({ vehicle }) => {
  const { t } = useTranslation(['m-vehicle']);
  const openDrawer = useVehicleDrawerStore(s => s.open);
  const handleViewClick = () => openDrawer(String(vehicle._id));

  const chip = vehicleStatusChip(t, vehicle.status);
  const typeLabel = t(`types.${vehicle.classification.type}`, {
    defaultValue: vehicle.classification.type,
  });
  const brandLabel = getVehicleBrandLabel(vehicle.classification.brand, t);
  const modelLine = `${brandLabel}${vehicle.classification.year ? ` · ${vehicle.classification.year}` : ''}`;

  return (
    <>
      <TableCell className="text-gray-600 body2">
        <div className="flex items-center gap-3">
          <MianAvatar
            src={vehicle.images?.[0]?.url ?? vehicle.images?.[0]?.upload ?? ''}
            className="size-[44px]"
            emptyFallback={
              <VehicleDynamicIcon
                vehicle={vehicle.classification.type}
                size="small"
                className="size-6 text-gray-700"
              />
            }
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-medium text-gray-900">{typeLabel}</span>
            <span className="truncate text-xs text-muted-foreground" dir="auto">
              {formatPlateForDisplay(vehicle.registration.plate)}
            </span>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <AvailabilityChip label={chip.label} tone={chip.tone} />
      </TableCell>
      <TableCell className="text-gray-600 body2">
        {vehicle.driver ? (
          <div className="flex flex-col gap-0.5 text-start">
            <div className="text-sm font-medium">{vehicle.driver.name}</div>
            <div className="text-xs text-muted-foreground">
              <span dir="ltr">{vehicle.driver.phone || ''}</span>
            </div>
          </div>
        ) : (
          <span className="text-sm font-medium text-red-500">{t('table.no_driver_assigned')}</span>
        )}
      </TableCell>
      <TableCell className="text-gray-600 body2">
        <span className="text-sm">{modelLine}</span>
      </TableCell>
      <TableCell onClick={e => e.stopPropagation()}>
        <CellAction type="view" onClick={handleViewClick} />
      </TableCell>
    </>
  );
};
